import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, errMessage, fileUrl } from "../api";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * RSVP. Capacity is enforced on the server with one atomic update, so the
 * worst that happens here is the reply comes back "waitlist" instead of
 * "confirmed" — which we say plainly rather than hiding.
 */
export default function RsvpForm({ gathering, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", seats: 1, note: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [failure, setFailure] = useState("");

  const set = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((x) => ({ ...x, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "This needs an answer.";
    if (!EMAIL.test(form.email.trim()))
      e.email = "That email doesn't look quite right.";
    const s = Number(form.seats);
    if (!Number.isInteger(s) || s < 1 || s > 8)
      e.seats = "Use a number (1\u20138 is plenty).";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setFailure("");
    if (!validate()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/gatherings/${gathering.id}/rsvp`, {
        name: form.name.trim(),
        email: form.email.trim(),
        seats: Number(form.seats),
        note: form.note.trim(),
      });
      setResult(data);
      if (onDone) onDone(data);
    } catch (err) {
      setFailure(errMessage(err, "We couldn't take that just now. Try again?"));
    } finally {
      setSending(false);
    }
  };

  /* ------------------------------------------------ after */
  if (result) {
    const { status } = result;
    if (status === "already_registered") {
      return (
        <div className="panel" data-testid="rsvp-already-registered">
          <h3>You&rsquo;re already on the list.</h3>
          <p>
            If you meant to change your party size, use the cancel link in your
            email and RSVP again. (It&rsquo;s clumsy. It works.)
          </p>
          <div className="actions">
            <a
              className="linkish"
              href={fileUrl(`/gatherings/${gathering.id}/calendar.ics`)}
              data-testid="rsvp-already-calendar-link"
            >
              Add it to your calendar &rarr;
            </a>
          </div>
        </div>
      );
    }
    if (status === "waitlist") {
      return (
        <div className="panel" data-testid="rsvp-waitlisted">
          <h3>You&rsquo;re on the waitlist.</h3>
          <p>If a seat opens, we&rsquo;ll email you. No need to hover.</p>
          <div className="actions">
            <Link className="linkish" to="/visit" data-testid="rsvp-waitlist-visit-link">
              Read what you&rsquo;d be walking into &rarr;
            </Link>
          </div>
        </div>
      );
    }
    return (
      <div className="panel" data-testid="rsvp-confirmed">
        <h3>You&rsquo;re in.</h3>
        <p>
          We&rsquo;ll expect you.
          {gathering.address_hidden
            ? " We'll send the address before the day."
            : ""}{" "}
          If plans change, you can cancel from your booking page.
        </p>
        <div className="actions">
          <a
            className="act"
            href={fileUrl(`/gatherings/${gathering.id}/calendar.ics`)}
            data-testid="rsvp-confirmed-calendar-link"
          >
            Add it to your calendar &rarr;
          </a>
          {result.token ? (
            <Link
              className="linkish"
              to={`/rsvp/${result.token}`}
              data-testid="rsvp-manage-link"
            >
              Manage this booking
            </Link>
          ) : null}
        </div>
      </div>
    );
  }

  /* ------------------------------------------------ form */
  const full = gathering.is_full;

  return (
    <form onSubmit={submit} noValidate data-testid="rsvp-form">
      <p className="eyebrow sc">
        <span className="num">&middot;</span>
        <span>{full ? "Join the waitlist" : "Tell us you're coming"}</span>
      </p>
      <h3>{full ? "It's full, but put your name down." : "Come to this one."}</h3>
      <p className="path-blurb">
        {full
          ? "People's plans change more often than you'd think. If a seat opens we'll write to you."
          : "Just a name and an email. There's nothing to sign and no portfolio."}
      </p>

      <div className="hp" aria-hidden="true">
        <label htmlFor="rsvp-hp">Leave this empty</label>
        <input id="rsvp-hp" tabIndex={-1} autoComplete="off" />
      </div>

      <div className="row">
        <div className="field">
          <label htmlFor="rsvp-name">Your name</label>
          <input
            id="rsvp-name"
            value={form.name}
            onChange={set("name")}
            autoComplete="name"
            aria-invalid={errors.name ? "true" : "false"}
            data-testid="rsvp-name-input"
          />
          {errors.name ? (
            <span className="err" data-testid="rsvp-name-error">
              <span className="lab">Note</span>
              {errors.name}
            </span>
          ) : null}
        </div>
        <div className="field">
          <label htmlFor="rsvp-email">Email</label>
          <input
            id="rsvp-email"
            type="email"
            inputMode="email"
            value={form.email}
            onChange={set("email")}
            autoComplete="email"
            aria-invalid={errors.email ? "true" : "false"}
            data-testid="rsvp-email-input"
          />
          {errors.email ? (
            <span className="err" data-testid="rsvp-email-error">
              <span className="lab">Note</span>
              {errors.email}
            </span>
          ) : null}
        </div>
      </div>

      <div className="field field-narrow">
        <label htmlFor="rsvp-seats">How many of you</label>
        <input
          id="rsvp-seats"
          type="number"
          min={1}
          max={8}
          value={form.seats}
          onChange={set("seats")}
          aria-invalid={errors.seats ? "true" : "false"}
          data-testid="rsvp-party-size-input"
        />
        {errors.seats ? (
          <span className="err" data-testid="rsvp-seats-error">
            <span className="lab">Note</span>
            {errors.seats}
          </span>
        ) : null}
      </div>

      <div className="field">
        <label htmlFor="rsvp-note">
          Anything we should know <span className="opt">&mdash; optional</span>
        </label>
        <textarea
          id="rsvp-note"
          rows={3}
          value={form.note}
          onChange={set("note")}
          data-testid="rsvp-note-textarea"
        />
      </div>

      <button
        type="submit"
        className="send"
        disabled={sending}
        data-testid="gathering-rsvp-submit-button"
      >
        {sending ? "Sending\u2026" : full ? "Put me on the waitlist" : "I'll be there"}
      </button>

      {failure ? (
        <div className="form-error" data-testid="rsvp-submit-error">
          {failure}
        </div>
      ) : null}

      <p className="form-note">
        Goes straight to Austin. No list, no automated anything &mdash; just a
        reply from a person.
      </p>
    </form>
  );
}
